import React, { useEffect, useState } from 'react';
import { Card, Button, Table, Modal, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useConfig } from "../utils/ConfigContext.jsx";
import { createAxiosInstance } from "../utils/AxiosInstance.jsx";

/* --------------------------------------------------
   Assign Modules Modal
-------------------------------------------------- */
const AssignModulesModal = ({ show, handleClose, role = {}, modules = [], onSave }) => {
    const [selectedModuleIds, setSelectedModuleIds] = useState([]);

    useEffect(() => {
        if (show && role?.id) {
            // Get module IDs from role's modules array or moduleIds
            const assignedModuleIds = role?.modules?.map(m => m.mm_id) || role?.moduleIds || [];
            setSelectedModuleIds(assignedModuleIds);
        }
    }, [role, show]);

    const handleSelectChange = (selectedOptions) => {
        const ids = selectedOptions.map(option => option.value);
        setSelectedModuleIds(ids);
    };

    const handleSave = () => {
        if (!role?.id) return;
        onSave(role.id, selectedModuleIds);
        handleClose();
    };

    // Format modules for react-select
    const moduleOptions = modules
        .filter(module => module.status !== 'Inactive')
        .map(module => ({
            value: module.mm_id,
            label: module.mm_module_name
        }));

    // Find selected options
    const selectedOptions = moduleOptions.filter(option =>
        selectedModuleIds.includes(option.value)
    );

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton className="bg-info text-white">
                <Modal.Title>Assign Modules to: {role?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Select Modules <span className="text-danger">*</span></Form.Label>
                        <Select
                            isMulti
                            options={moduleOptions}
                            value={selectedOptions}
                            onChange={handleSelectChange}
                            placeholder="Select modules..."
                            isClearable
                            isSearchable
                            noOptionsMessage={() => 'No modules found'}
                        />
                        <small className="text-muted d-block mt-2">Search and select multiple modules</small>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="info" onClick={handleSave}>Save Assignments</Button>
            </Modal.Footer>
        </Modal>
    );
};

/* --------------------------------------------------
   Roles Card
-------------------------------------------------- */
const RolesCard = ({ modules: propModules, AxiosInstance }) => {
    const emptyRole = { name: '', description: '', type: '', status: 1 };

    const [roles, setRoles] = useState([]);
    const [modules, setModules] = useState(propModules || []);
    const [loading, setLoading] = useState(true);

    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentRole, setCurrentRole] = useState(emptyRole);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [roleToAssign, setRoleToAssign] = useState({});

    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');

    /* Fetch roles */
    const getRoles = async () => {
        try {
            const res = await AxiosInstance.get('/roles');
            setRoles(res.data?.data || []);
        } catch (error) {
            console.error("Error fetching roles:", error);
        } finally {
            setLoading(false);
        }
    };

    /* Fetch modules */
    const getModules = async () => {
        try {
            const res = await AxiosInstance.get('/modules/assignable');
            setModules(res.data?.data || []);
        } catch (error) {
            console.error("Error fetching modules:", error);
        }
    };

    useEffect(() => {
        getRoles();
        getModules();
    }, []);

    const handleEditShow = (role = emptyRole) => {
        setIsEditing(!!role.id);
        setCurrentRole(role || emptyRole);
        setErrors({});
        setSubmitError('');
        setShowEditModal(true);
    };

    const handleEditClose = () => {
        setShowEditModal(false);
        setErrors({});
        setSubmitError('');
        setCurrentRole(emptyRole);
    };

    const handleAssignShow = (role) => {
        if (!role?.id) return;
        setRoleToAssign(role);
        setShowAssignModal(true);
    };

    const handleAssignClose = () => {
        setShowAssignModal(false);
        setRoleToAssign({});
    };

    const handleSaveAssignments = async (roleId, newModuleIds) => {
        try {
            await AxiosInstance.post(`/role_modules`, {
                role_id: roleId,
                module_ids: newModuleIds
            });

            // Update the role with the new modules
            setRoles(prev =>
                prev.map(r => {
                    if (r.id === roleId) {
                        // Update the modules array from the modules state
                        const updatedModules = modules.filter(m => newModuleIds.includes(m.mm_id));
                        return { ...r, modules: updatedModules, moduleIds: newModuleIds };
                    }
                    return r;
                })
            );
            alert('Module Assigned Successfully!')
        } catch (error) {
            console.error("Error saving assignments:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};

        if (!currentRole.name || !currentRole.name.trim()) {
            newErrors.name = 'Role name is required';
        } else if (currentRole.name.trim().length < 2) {
            newErrors.name = 'Role name must be at least 2 characters';
        } else if (currentRole.name.trim().length > 50) {
            newErrors.name = 'Role name must not exceed 50 characters';
        }

        if (!currentRole.type) {
            newErrors.type = 'Role type is required';
        }

        if (currentRole.status === undefined || currentRole.status === null || currentRole.status === '') {
            newErrors.status = 'Role status is required';
        }

        if (currentRole.description && currentRole.description.trim().length > 100) {
            newErrors.description = 'Description must not exceed 100 characters';
        }

        if (currentRole.name) {
            const duplicate = roles.some(role =>
                role.name?.toLowerCase() === currentRole.name.trim().toLowerCase() &&
                (!isEditing || role.id !== currentRole.id)
            );
            if (duplicate) {
                newErrors.name = 'A role with this name already exists';
            }
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        try {
            setSubmitError('');

            const roleData = {
                name: currentRole.name.trim(),
                description: currentRole.description?.trim() || '',
                type: currentRole.type,
                status: parseInt(currentRole.status),
            };

            if (isEditing) {
                const res = await AxiosInstance.put(`/roles/${currentRole.id}`, roleData);
                setRoles(prev =>
                    prev.map(r => r.id === currentRole.id ? res.data.data : r)
                );
            } else {
                const res = await AxiosInstance.post('/roles', roleData);
                setRoles(prev => [...prev, res.data.data]);
                
                // Show assign modules modal for newly created role
                setRoleToAssign(res.data.data);
                setShowEditModal(false);
                setCurrentRole(emptyRole);
                setShowAssignModal(true);
                return;
            }

            setShowEditModal(false);
            setCurrentRole(emptyRole);
        } catch (error) {
            console.error("Error saving role:", error);
            setSubmitError(error.response?.data?.message || 'Error saving role');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this role?')) return;
        try {
            await AxiosInstance.delete(`/roles/${id}`);
            setRoles(prev => prev.filter(r => r.id !== id));
        } catch (error) {
            console.error("Error deleting role:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentRole(prev => ({ ...prev, [name]: value }));
    };

    if (loading) return <p>Loading roles...</p>;

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center  text-white">
                <h5 className="mb-0">Role List</h5>
                <Button variant="light bg-primary text-white" onClick={() => handleEditShow()}>
                    <i className="ri-add-circle-line me-2"></i> Add New Role
                </Button>
            </Card.Header>

            <Card.Body>
                <Table striped bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.map(role => (
                            <tr key={role.id}>
                                <td>{role.name}</td>
                                <td>{role.description}</td>
                                <td>{role.type ? role.type.toUpperCase() : 'N/A'}</td>
                                <td>
                                    <span className={`badge ${role.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                        {role.status === 1 ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <Button variant="outline-info" size="sm" className="me-2"
                                        onClick={() => handleAssignShow(role)}>
                                        <i className="ri-lock-2-line"></i> Assign Modules
                                    </Button>
                                    <Button variant="outline-primary" size="sm" className="me-2"
                                        onClick={() => handleEditShow(role)}>
                                        <i className="ri-edit-line"></i>
                                    </Button>
                                    <Button variant="outline-danger" size="sm"
                                        onClick={() => handleDelete(role.id)}>
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                {/* Role Add/Edit Modal */}
                <Modal show={showEditModal} onHide={handleEditClose}>
                    <Modal.Header closeButton className="bg-primary text-white">
                        <Modal.Title>{isEditing ? 'Edit Role' : 'Add New Role'}</Modal.Title>
                    </Modal.Header>

                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            {submitError && (
                                <div className="alert alert-danger alert-dismissible fade show">
                                    {submitError}
                                    <button type="button" className="btn-close"
                                        onClick={() => setSubmitError('')}></button>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={currentRole.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    maxLength="50"
                                />
                                {errors.name &&
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                }
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={currentRole.description}
                                    onChange={handleChange}
                                    isInvalid={!!errors.description}
                                    maxLength="100"
                                />
                                {errors.description &&
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                }
                                <small className="text-muted">
                                    Max 100 characters. {(currentRole.description || '').length}/100
                                </small>
                            </Form.Group>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="type"
                                            value={currentRole.type}
                                            onChange={handleChange}
                                            isInvalid={!!errors.type}
                                        >
                                            <option value="">Select Type</option>
                                            <option value="system">System</option>
                                            <option value="custom">Custom</option>
                                        </Form.Select>
                                        {errors.type &&
                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                {errors.type}
                                            </Form.Control.Feedback>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={currentRole.status}
                                            onChange={handleChange}
                                            isInvalid={!!errors.status}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </Form.Select>
                                        {errors.status &&
                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                {errors.status}
                                            </Form.Control.Feedback>
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleEditClose}>Close</Button>
                            <Button variant="primary" type="submit">
                                {isEditing ? 'Save Changes' : 'Create Role'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Assign Modules Modal */}
                <AssignModulesModal
                    show={showAssignModal}
                    handleClose={handleAssignClose}
                    role={roleToAssign}
                    modules={modules}
                    onSave={handleSaveAssignments}
                />
            </Card.Body>
        </Card>
    );
};

/* --------------------------------------------------
   Main Page
-------------------------------------------------- */
const Roles = () => {
    const { API_BASE_URL } = useConfig();
    const AxiosInstance = createAxiosInstance(API_BASE_URL);

    return (
        <>
            <style>{`
                .modal-backdrop {
                    height: 100% !important;
                    width: 100% !important;
                }
            `}</style>

            <div className="mb-5 pb-5">
                <Row className="g-4">
                    <Col lg={12}>
                        <RolesCard modules={[]} AxiosInstance={AxiosInstance} />
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default Roles;
